import { useEffect, useMemo, useState } from "react";
import {
  Archive,
  Download,
  Upload,
  KeyRound,
  Server,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  Shield,
  Image as ImageIcon,
  HardDrive,
  Zap,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const Lbl = ({ children }) => (
  <label className="text-xs uppercase tracking-[0.15em] text-ash">{children}</label>
);

const fmtSize = (bytes) => {
  if (!bytes || bytes <= 0) return "—";
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} Ko`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb.toFixed(1)} Mo`;
  return `${(mb / 1024).toFixed(2)} Go`;
};

function Tabs({ tabs, active, onChange }) {
  return (
    <div className="border-b border-stone mb-6 flex flex-wrap gap-1">
      {tabs.map((t) => (
        <button
          key={t.id}
          onClick={() => onChange(t.id)}
          data-testid={`backup-tab-${t.id}`}
          className={`px-4 py-3 text-xs uppercase tracking-[0.15em] -mb-px border-b-2 transition-colors ${
            active === t.id
              ? "border-copper text-ink"
              : "border-transparent text-ash hover:text-ink"
          }`}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

/* -------------------- Snapshot section (full backup) -------------------- */
function SnapshotSection({ options }) {
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [loading, setLoading] = useState(false);
  const [encryptionMode, setEncryptionMode] = useState("password");

  // Estimation taille approximative
  const totalFiles = (options?.modules || []).reduce(
    (acc, m) => acc + (m.files_count || 0),
    0
  );
  const totalSize = (options?.modules || []).reduce(
    (acc, m) => acc + (m.size_bytes || 0),
    0
  );

  const doSnapshot = async () => {
    if (encryptionMode === "password") {
      if (!password || password.length < 6) {
        toast.error("Mot de passe requis (6 caractères minimum).");
        return;
      }
      if (password !== password2) {
        toast.error("Les mots de passe ne correspondent pas.");
        return;
      }
    } else if (encryptionMode === "server" && !options?.server_key_set) {
      toast.error("Aucune clé serveur configurée (BACKUP_SECRET).");
      return;
    }
    const ok = window.confirm(
      "Cela va générer une archive COMPLÈTE (données MongoDB + secrets .env + photos produits) permettant de redéployer intégralement l'application. Cette archive doit être conservée en lieu sûr. Continuer ?"
    );
    if (!ok) return;

    setLoading(true);
    try {
      const res = await api.post(
        "/superadmin/backup/snapshot",
        {
          mode: encryptionMode,
          password: encryptionMode === "password" ? password : null,
        },
        { responseType: "blob" }
      );
      const blob = new Blob([res.data], { type: "application/zip" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers?.["content-disposition"] || "";
      const match = /filename="?([^"]+)"?/.exec(cd);
      a.download = match ? match[1] : `lumiere-snapshot-${Date.now()}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Snapshot complet téléchargé.");
    } catch (e) {
      let msg = "Erreur lors du snapshot";
      if (e?.response?.data instanceof Blob) {
        try {
          const text = await e.response.data.text();
          const json = JSON.parse(text);
          msg = formatApiError(json?.detail) || msg;
        } catch { /* ignore */ }
      } else {
        msg = formatApiError(e?.response?.data?.detail) || msg;
      }
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      className="border-2 border-copper/40 bg-gradient-to-br from-copper/5 to-transparent p-6 rounded-sm mb-8"
      data-testid="snapshot-section"
    >
      <div className="flex items-start gap-4 mb-4">
        <div className="p-2.5 bg-copper text-cream rounded-sm">
          <Zap className="h-6 w-6" strokeWidth={1.5} />
        </div>
        <div className="flex-1">
          <h3 className="font-serif text-2xl text-ink">Sauvegarde complète (« snapshot »)</h3>
          <p className="text-sm text-graphite mt-1">
            Génère une archive unique contenant <strong>tout</strong> (données MongoDB + clés API .env + photos produits).
            Idéal pour figer une version stable de l&apos;application et pouvoir la redéployer plus tard.
          </p>
          <div className="flex items-center gap-4 text-xs text-ash mt-2">
            <span className="flex items-center gap-1">
              <HardDrive className="h-3 w-3" /> ~{fmtSize(totalSize)} estimé
            </span>
            <span className="flex items-center gap-1">
              <ImageIcon className="h-3 w-3" /> {totalFiles} fichiers média
            </span>
          </div>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3 mb-3">
        <button
          type="button"
          onClick={() => setEncryptionMode("password")}
          data-testid="snapshot-enc-password"
          className={`flex items-center gap-2 p-3 border text-left text-xs transition-colors ${
            encryptionMode === "password"
              ? "border-copper bg-white"
              : "border-stone hover:border-ash bg-white/60"
          }`}
        >
          <KeyRound className="h-4 w-4 text-copper" />
          <span className="font-medium">Mot de passe</span>
        </button>
        <button
          type="button"
          onClick={() => setEncryptionMode("server")}
          data-testid="snapshot-enc-server"
          disabled={!options?.server_key_set}
          className={`flex items-center gap-2 p-3 border text-left text-xs transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
            encryptionMode === "server"
              ? "border-copper bg-white"
              : "border-stone hover:border-ash bg-white/60"
          }`}
        >
          <Server className="h-4 w-4 text-copper" />
          <span className="font-medium">Clé serveur (BACKUP_SECRET)</span>
        </button>
      </div>

      {encryptionMode === "password" && (
        <div className="grid sm:grid-cols-2 gap-3 mb-4">
          <Input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Mot de passe (min 6 car.)"
            data-testid="snapshot-password"
          />
          <Input
            type="password"
            value={password2}
            onChange={(e) => setPassword2(e.target.value)}
            placeholder="Confirmer"
            data-testid="snapshot-password-confirm"
          />
        </div>
      )}

      <button
        onClick={doSnapshot}
        disabled={loading}
        data-testid="snapshot-btn"
        className="w-full sm:w-auto flex items-center justify-center gap-2 bg-copper text-cream px-6 py-3 text-xs uppercase tracking-[0.15em] hover:bg-ink transition-colors disabled:opacity-50"
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
        Télécharger le snapshot complet
      </button>
    </section>
  );
}

/* -------------------- Export Tab -------------------- */
function ExportTab({ options }) {
  const [selected, setSelected] = useState({});
  const [encryptionMode, setEncryptionMode] = useState("password");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (options?.modules) {
      const init = {};
      options.modules.forEach((m) => {
        // Sélectionne tout par défaut sauf 'secrets' (sensible)
        init[m.id] = m.id !== "secrets";
      });
      setSelected(init);
    }
  }, [options]);

  const allChecked = useMemo(
    () => options?.modules?.every((m) => selected[m.id]) || false,
    [selected, options]
  );

  const toggleAll = (val) => {
    const next = {};
    options.modules.forEach((m) => (next[m.id] = val));
    setSelected(next);
  };

  const selectedIds = Object.entries(selected)
    .filter(([_, v]) => v)
    .map(([k]) => k);

  const exportNow = async () => {
    if (selectedIds.length === 0) {
      toast.error("Sélectionnez au moins un module.");
      return;
    }
    if (encryptionMode === "password") {
      if (!password || password.length < 6) {
        toast.error("Mot de passe requis (6 caractères minimum).");
        return;
      }
      if (password !== password2) {
        toast.error("Les mots de passe ne correspondent pas.");
        return;
      }
    } else if (encryptionMode === "server" && !options?.server_key_set) {
      toast.error("Aucune clé serveur configurée (BACKUP_SECRET).");
      return;
    }
    if (selected.secrets) {
      const ok = window.confirm(
        "Vous incluez les clés API & secrets dans la sauvegarde. Le fichier sera bien chiffré, mais à manipuler avec précaution. Continuer ?"
      );
      if (!ok) return;
    }

    setLoading(true);
    try {
      const res = await api.post(
        "/superadmin/backup/export",
        {
          modules: selectedIds,
          encryption: {
            mode: encryptionMode,
            password: encryptionMode === "password" ? password : null,
          },
        },
        { responseType: "blob" }
      );
      const blob = new Blob([res.data], { type: "application/zip" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers?.["content-disposition"] || "";
      const match = /filename="?([^"]+)"?/.exec(cd);
      a.download = match ? match[1] : `lumiere-backup-${Date.now()}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Sauvegarde téléchargée.");
    } catch (e) {
      // Pour blob, le detail est dans le blob
      let msg = "Erreur lors de l'export";
      if (e?.response?.data instanceof Blob) {
        try {
          const text = await e.response.data.text();
          const json = JSON.parse(text);
          msg = formatApiError(json?.detail) || msg;
        } catch {
          /* ignore */
        }
      } else {
        msg = formatApiError(e?.response?.data?.detail) || msg;
      }
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      <SnapshotSection options={options} />

      {/* Modules à inclure */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-2xl">Contenu de la sauvegarde</h3>
          <p className="text-xs text-ash">
            Ou sélectionnez précisément ce que vous souhaitez inclure ↓
          </p>
        </div>
        <div className="flex items-center justify-end mb-3">
          <button
            type="button"
            onClick={() => toggleAll(!allChecked)}
            data-testid="backup-toggle-all"
            className="text-xs uppercase tracking-[0.15em] text-copper hover:underline"
          >
            {allChecked ? "Tout désélectionner" : "Tout sélectionner"}
          </button>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          {options?.modules?.map((m) => (
            <label
              key={m.id}
              data-testid={`backup-module-${m.id}`}
              className={`flex items-start gap-3 p-4 border cursor-pointer transition-colors ${
                selected[m.id] ? "border-copper bg-copper/5" : "border-stone hover:border-ash"
              }`}
            >
              <input
                type="checkbox"
                checked={!!selected[m.id]}
                onChange={(e) =>
                  setSelected((s) => ({ ...s, [m.id]: e.target.checked }))
                }
                className="mt-1"
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-ink">{m.label}</span>
                  {m.id === "secrets" && (
                    <span className="text-[10px] uppercase tracking-[0.15em] bg-yellow-100 text-yellow-800 px-2 py-0.5">
                      Sensible
                    </span>
                  )}
                </div>
                {m.collections?.length > 0 && (
                  <p className="text-xs text-ash mt-1">
                    Collections : {m.collections.join(", ")}
                  </p>
                )}
                {m.media && (
                  <p className="text-xs text-ash mt-1 flex items-center gap-1">
                    <ImageIcon className="h-3 w-3" />
                    {m.files_count || 0} fichier{(m.files_count || 0) > 1 ? "s" : ""} · {fmtSize(m.size_bytes)}
                  </p>
                )}
                {m.id === "secrets" && (
                  <p className="text-xs text-ash mt-1">
                    Inclut Stripe, Resend, JWT secret, identifiants admin/superadmin…
                  </p>
                )}
              </div>
            </label>
          ))}
        </div>
      </section>

      {/* Chiffrement */}
      <section>
        <h3 className="font-serif text-2xl mb-4">Chiffrement</h3>
        <div className="grid sm:grid-cols-2 gap-3 mb-4">
          <button
            type="button"
            onClick={() => setEncryptionMode("password")}
            data-testid="backup-enc-password"
            className={`flex items-center gap-3 p-4 border text-left transition-colors ${
              encryptionMode === "password"
                ? "border-copper bg-copper/5"
                : "border-stone hover:border-ash"
            }`}
          >
            <KeyRound className="h-5 w-5 text-copper" strokeWidth={1.4} />
            <div>
              <div className="font-medium text-ink">Mot de passe</div>
              <div className="text-xs text-ash">{"Saisi à l'export et requis à l'import."}</div>
            </div>
          </button>
          <button
            type="button"
            onClick={() => setEncryptionMode("server")}
            data-testid="backup-enc-server"
            className={`flex items-center gap-3 p-4 border text-left transition-colors ${
              encryptionMode === "server"
                ? "border-copper bg-copper/5"
                : "border-stone hover:border-ash"
            }`}
          >
            <Server className="h-5 w-5 text-copper" strokeWidth={1.4} />
            <div>
              <div className="font-medium text-ink">Clé serveur (.env)</div>
              <div className="text-xs text-ash">
                {options?.server_key_set
                  ? "Clé BACKUP_SECRET configurée."
                  : "BACKUP_SECRET non configurée — indisponible."}
              </div>
            </div>
          </button>
        </div>

        {encryptionMode === "password" && (
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Lbl>Mot de passe</Lbl>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="6 caractères minimum"
                data-testid="backup-password"
                className="mt-1"
              />
            </div>
            <div>
              <Lbl>Confirmer</Lbl>
              <Input
                type="password"
                value={password2}
                onChange={(e) => setPassword2(e.target.value)}
                data-testid="backup-password-confirm"
                className="mt-1"
              />
            </div>
          </div>
        )}
      </section>

      <div className="flex items-center justify-between pt-4 border-t border-stone">
        <p className="text-xs text-ash">
          Format : ZIP chiffré AES-256 (WinZip). Conservez le fichier et le mot de passe en lieu sûr.
        </p>
        <button
          onClick={exportNow}
          disabled={loading}
          data-testid="backup-export-btn"
          className="flex items-center gap-2 bg-ink text-cream px-6 py-3 text-xs uppercase tracking-[0.15em] disabled:opacity-50"
        >
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Download className="h-4 w-4" />
          )}
          Télécharger la sauvegarde
        </button>
      </div>
    </div>
  );
}

/* -------------------- Import Tab -------------------- */
function ImportTab({ options }) {
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState("merge");
  const [encryptionMode, setEncryptionMode] = useState("password");
  const [password, setPassword] = useState("");
  const [restoreSecrets, setRestoreSecrets] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    if (!file) {
      toast.error("Sélectionnez un fichier de sauvegarde.");
      return;
    }
    if (encryptionMode === "password" && (!password || password.length < 6)) {
      toast.error("Mot de passe requis pour déchiffrer.");
      return;
    }
    const warn =
      mode === "replace"
        ? "⚠️ Mode REMPLACER : toutes les données existantes des modules concernés seront EFFACÉES puis remplacées par celles du fichier. Cette action est IRRÉVERSIBLE.\n\nConfirmer la restauration en mode REMPLACER ?"
        : "Confirmer la restauration en mode FUSIONNER (les entrées existantes seront mises à jour) ?";
    if (!window.confirm(warn)) return;

    if (restoreSecrets) {
      const ok = window.confirm(
        "Vous allez aussi remplacer les clés API & secrets du .env. Le backend devra être redémarré. Continuer ?"
      );
      if (!ok) return;
    }

    setLoading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mode", mode);
      fd.append("encryption_mode", encryptionMode);
      if (encryptionMode === "password") fd.append("password", password);
      fd.append("restore_secrets", restoreSecrets ? "true" : "false");

      const res = await api.post("/superadmin/backup/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(res.data);
      toast.success("Sauvegarde restaurée.");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Erreur lors de l'import");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      <section>
        <h3 className="font-serif text-2xl mb-4">Fichier de sauvegarde</h3>
        <label
          className="flex flex-col items-center justify-center border-2 border-dashed border-stone hover:border-copper p-10 cursor-pointer transition-colors"
          data-testid="backup-file-drop"
        >
          <Upload className="h-10 w-10 text-ash mb-3" strokeWidth={1.2} />
          <span className="text-sm text-ink">
            {file ? file.name : "Cliquez ou glissez un fichier .zip"}
          </span>
          {file && (
            <span className="text-xs text-ash mt-1">
              {(file.size / 1024).toFixed(1)} Ko
            </span>
          )}
          <input
            type="file"
            accept=".zip,application/zip"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="hidden"
            data-testid="backup-file-input"
          />
        </label>
      </section>

      <section>
        <h3 className="font-serif text-2xl mb-4">Mode de restauration</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => setMode("merge")}
            data-testid="backup-mode-merge"
            className={`p-4 border text-left transition-colors ${
              mode === "merge" ? "border-copper bg-copper/5" : "border-stone hover:border-ash"
            }`}
          >
            <div className="font-medium text-ink">Fusionner</div>
            <p className="text-xs text-ash mt-1">
              Les entrées du fichier sont ajoutées ou mises à jour. Les données existantes non
              présentes dans la sauvegarde sont conservées.
            </p>
          </button>
          <button
            type="button"
            onClick={() => setMode("replace")}
            data-testid="backup-mode-replace"
            className={`p-4 border text-left transition-colors ${
              mode === "replace" ? "border-copper bg-copper/5" : "border-stone hover:border-ash"
            }`}
          >
            <div className="font-medium text-ink flex items-center gap-2">
              Remplacer
              <AlertTriangle className="h-4 w-4 text-yellow-700" />
            </div>
            <p className="text-xs text-ash mt-1">
              <strong>Efface toutes les données</strong> des modules concernés et les remplace
              par le contenu du fichier. Action irréversible.
            </p>
          </button>
        </div>
      </section>

      <section>
        <h3 className="font-serif text-2xl mb-4">Déchiffrement</h3>
        <div className="grid sm:grid-cols-2 gap-3 mb-4">
          <button
            type="button"
            onClick={() => setEncryptionMode("password")}
            data-testid="backup-import-enc-password"
            className={`flex items-center gap-3 p-4 border text-left transition-colors ${
              encryptionMode === "password"
                ? "border-copper bg-copper/5"
                : "border-stone hover:border-ash"
            }`}
          >
            <KeyRound className="h-5 w-5 text-copper" strokeWidth={1.4} />
            <div>
              <div className="font-medium text-ink">Mot de passe</div>
              <div className="text-xs text-ash">{"Celui utilisé à l'export."}</div>
            </div>
          </button>
          <button
            type="button"
            onClick={() => setEncryptionMode("server")}
            data-testid="backup-import-enc-server"
            className={`flex items-center gap-3 p-4 border text-left transition-colors ${
              encryptionMode === "server"
                ? "border-copper bg-copper/5"
                : "border-stone hover:border-ash"
            }`}
          >
            <Server className="h-5 w-5 text-copper" strokeWidth={1.4} />
            <div>
              <div className="font-medium text-ink">Clé serveur</div>
              <div className="text-xs text-ash">
                {options?.server_key_set
                  ? "BACKUP_SECRET configurée."
                  : "BACKUP_SECRET non configurée."}
              </div>
            </div>
          </button>
        </div>
        {encryptionMode === "password" && (
          <div>
            <Lbl>Mot de passe</Lbl>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="backup-import-password"
              className="mt-1 max-w-md"
            />
          </div>
        )}
      </section>

      <section>
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={restoreSecrets}
            onChange={(e) => setRestoreSecrets(e.target.checked)}
            data-testid="backup-restore-secrets"
            className="mt-1"
          />
          <div>
            <div className="font-medium text-ink flex items-center gap-2">
              <Shield className="h-4 w-4 text-yellow-700" />
              Restaurer aussi les clés API & secrets (.env)
            </div>
            <p className="text-xs text-ash mt-1">
              {"À utiliser uniquement lors d'une migration. Le backend devra être redémarré ensuite."}
            </p>
          </div>
        </label>
      </section>

      <div className="flex items-center justify-end pt-4 border-t border-stone">
        <button
          onClick={submit}
          disabled={loading || !file}
          data-testid="backup-import-btn"
          className="flex items-center gap-2 bg-ink text-cream px-6 py-3 text-xs uppercase tracking-[0.15em] disabled:opacity-50"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          Restaurer la sauvegarde
        </button>
      </div>

      {result && (
        <div
          className="border border-green-200 bg-green-50 p-5"
          data-testid="backup-import-result"
        >
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 className="h-5 w-5 text-green-700" />
            <h4 className="font-medium text-ink">Restauration terminée</h4>
          </div>
          <div className="text-xs text-ash space-y-2">
            <div>
              Mode : <strong>{result.stats?.mode}</strong> • Modules :{" "}
              {(result.stats?.modules || []).join(", ")}
            </div>
            {result.stats?.restored && (
              <div>
                Documents restaurés :{" "}
                {Object.entries(result.stats.restored)
                  .map(([k, v]) => `${k}=${v}`)
                  .join(", ")}
              </div>
            )}
            {result.stats?.skipped && (
              <div>
                {"Ignorés (conflit d'index unique) : "}
                {Object.entries(result.stats.skipped)
                  .map(([k, v]) => `${k}=${v}`)
                  .join(", ")}
              </div>
            )}
            {result.stats?.secrets_restored !== undefined && (
              <div>
                Secrets restaurés : {result.stats.secrets_restored}.{" "}
                <em>{result.stats.secrets_note}</em>
              </div>
            )}
            {result.stats?.media_restored && (
              <div className="flex items-start gap-1">
                <ImageIcon className="h-3.5 w-3.5 mt-0.5" />
                <span>
                  Photos restaurées :{" "}
                  {Object.entries(result.stats.media_restored)
                    .map(([k, v]) => `${k}=${v.restored || 0}${v.skipped ? ` (${v.skipped} ignorés)` : ""}`)
                    .join(", ")}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* -------------------- Main -------------------- */
export default function SuperBackup() {
  const [tab, setTab] = useState("export");
  const [options, setOptions] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/superadmin/backup/options");
        setOptions(res.data);
      } catch (e) {
        toast.error(formatApiError(e?.response?.data?.detail) || "Erreur de chargement");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div data-testid="super-backup-page">
      <div className="flex items-center gap-3 mb-8">
        <Archive className="h-7 w-7 text-copper" strokeWidth={1.2} />
        <div>
          <h1 className="font-serif text-4xl">Sauvegarde & Restauration</h1>
          <p className="text-sm text-ash mt-1">
            Exporte un fichier chiffré complet ou partiel du site. Réimportable à tout moment.
          </p>
        </div>
      </div>

      <Tabs
        tabs={[
          { id: "export", label: "Exporter" },
          { id: "import", label: "Importer" },
        ]}
        active={tab}
        onChange={setTab}
      />

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-copper" />
        </div>
      ) : tab === "export" ? (
        <ExportTab options={options} />
      ) : (
        <ImportTab options={options} />
      )}
    </div>
  );
}
